import type { SwappedTheme } from '@funkit/connect-core'

import type { FunkitTheme } from '../theme/toRestyleTheme'

const px = (value: number) => `${value}px`
const toRawHex = (color: string) => color.replace(/^#/, '')

/**
 * Loading-state theme for the Swapped embed: the query params sent to `/fops`
 * (`thirdPartyPaymentConfigs.swapped.themeConfig`) that the API bakes into the
 * returned `embeddedFlowUrl` (e.g. `…&iframeBackground=fbf9f4&textColor=282828`)
 * — the widget themes its boot spinner / skeleton straight from the URL, before
 * its message listener is up and any `SWAPPED_THEME` postMessage can land.
 * Mirrors web's `buildSwappedThemeQueryParams(resolvedTheme)`; colors are raw
 * hex (no `#`, they travel in a URL).
 *
 * Web also pins `height`/`width` to its dialog constants; omitted here — the
 * widget sizes to the WebView's real viewport.
 */
export function buildSwappedThemeQueryParamsRN(
  theme: FunkitTheme,
): Record<string, string> {
  const { background, content, border, borderRadii } = theme
  return {
    iframeBackground: toRawHex(background.modal),
    widgetBackground: toRawHex(background.modal),
    textColor: toRawHex(content.elements.primary),
    spinnerColor: toRawHex(content.elements.primary),
    modalRadii: px(borderRadii.modal),
    skeletonColor: toRawHex(border.elements.default.color),
    skeletonSecondaryColor: toRawHex(background.secondary),
  }
}

/**
 * Builds the {@link SwappedTheme} payload for the embedded Swapped flow from
 * the active Restyle theme — the React Native counterpart of web's
 * `buildSwappedTheme(ThemeVars)`. RN tokens are concrete hex / numbers (no
 * CSS-var indirection), so unlike web no `resolveObjectCssVars` pass is needed.
 *
 * The Swapped widget's vocabulary is richer than the Fun Variables export: it
 * has hover / focused / disabled slots the mobile design doesn't define. Since
 * connect-rn's tokens are a strict mirror of Figma (only default / pressed /
 * some disabled states exist), those extra slots are adapted here at the
 * boundary: hover ← pressed, primary/tertiary disabled ← the default fill (the
 * widget dims via opacity), and the widget's neutral "focused" strokes map onto
 * the `border/elements/{default,selected}` variables.
 *
 * Fonts are intentionally omitted: Swapped expects a slug from its hosted
 * webfont set, and RN font families (system/native names) don't map onto it.
 */
export function buildSwappedThemeRN(
  theme: FunkitTheme,
  isDarkMode: boolean,
): SwappedTheme {
  const {
    background,
    content,
    border,
    brand,
    borderRadii,
    sizes,
    spacing,
    textVariants,
  } = theme
  return {
    colors: {
      // ── Core text ─────────────────────────────────────────────────────
      primaryText: content.elements.primary,
      secondaryText: content.elements.secondary,
      tertiaryText: content.elements.secondary,

      // ── Surfaces ──────────────────────────────────────────────────────
      modalBackground: background.modal,
      offBackground: background.secondary,
      hoverState: background.secondary,
      lightStroke: border.elements.default.color,
      mediumStroke: border.elements.default.color,
      heavyStroke: border.elements.default.color,
      modalBorder: border.modal.default,
      modalHeaderDivider: border.elements.default.color,
      modalFooterDivider: border.elements.default.color,

      // ── Accent / brand ────────────────────────────────────────────────
      actionColor: brand.primary,
      accentColor: brand.primary,
      accentColorForeground: content.button.primary.default,

      // ── Status ────────────────────────────────────────────────────────
      error: content.elements.error,
      success: content.elements.success,

      // ── Spinner ───────────────────────────────────────────────────────
      spinnerIndicator: content.elements.primary,
      spinnerBackground: border.elements.default.color,

      // ── Primary button (hover ← pressed, disabled ← default fill) ─────
      // web spreads both `buttonBackground*` (ThemeVars) and `buttonPrimary*`
      // (widget aliases); the widget reads whichever is present.
      buttonBackground: background.button.primary.default,
      buttonBackgroundHover: background.button.primary.pressed,
      buttonBackgroundPressed: background.button.primary.pressed,
      buttonBackgroundDisabled: background.button.primary.default,
      buttonPrimary: background.button.primary.default,
      buttonPrimaryHover: background.button.primary.pressed,
      buttonDisabled: background.button.primary.default,
      buttonTextPrimary: content.button.primary.default,
      buttonTextHover: content.button.primary.default,
      buttonTextDisabled: content.button.primary.default,
      buttonTextSecondary: content.button.secondary.default,
      buttonTextSecondaryDisabled: content.button.secondary.default,
      buttonFocusedOutline: 'none',
      buttonBorderGradient: 'none',
      buttonHoverBorderGradient: 'none',
      buttonDisabledBorderGradient: 'none',

      // ── Tertiary button (hover ← pressed, disabled ← default fill) ────
      buttonBackgroundTertiary: background.button.tertiary.default,
      buttonBackgroundHoverTertiary: background.button.tertiary.pressed,
      buttonBackgroundDisabledTertiary: background.button.tertiary.default,
      buttonTextTertiary: content.button.tertiary.default,
      buttonTextDisabledTertiary: content.button.tertiary.default,
      buttonBorderTertiary: 'transparent',
      buttonBorderFocusedTertiary: border.elements.selected.color,

      // ── Icon button / top bar (hover ← pressed) ───────────────────────
      headerButtonBackground: background.button.icon.default,
      buttonIconBackgroundHover: background.button.icon.pressed,
      buttonIconBackgroundPressed: background.button.icon.pressed,
      buttonIconStroke: content.button.icon.default,
      modalTopbarIcon: content.button.icon.default,
      modalTopbarIconBackground: background.button.icon.default,
      modalTopbarIconBackgroundHover: background.button.icon.pressed,
      modalTopbarIconBackgroundPressed: background.button.icon.pressed,

      // ── Input (hover/focused ← border/elements variables) ─────────────
      inputBackground: background.modal,
      inputBackgroundHover: background.secondary,
      inputBorderBase: border.elements.default.color,
      inputBorderHover: border.elements.selected.color,
      inputLabel: content.elements.primary,
      inputBorder: border.elements.default.color,
      inputBorderFocused: content.elements.secondary,
      inputBorderActive: content.elements.secondary,
      inputPlaceholder: content.elements.secondary,

      // ── Selection ─────────────────────────────────────────────────────
      selectedOptionBorder: border.elements.selected.color,
      focusedOptionBorder: border.elements.selected.color,

      // ── Widget aliases (mirrors web buildSwappedTheme) ────────────────
      boxBackground: background.secondary,
      secondaryButtonBg: background.button.tertiary.default,
      secondaryButtonText: content.button.tertiary.default,
      secondaryButtonHover: background.button.tertiary.pressed,
      secondaryButtonTextHover: content.button.tertiary.default,
    },
    radii: {
      modal: px(borderRadii.modal),
      actionButton: px(borderRadii.button),
      summaryBox: px(borderRadii.element),
      box: px(borderRadii.element),
      otpInput: px(borderRadii.button),
      calculatorInput: px(borderRadii.button),
      amountPill: px(borderRadii.button),
    },
    paymentMethodLightLogos: !isDarkMode,
    headerHeight: px(sizes.topBar),
    headerFontWeight: String(textVariants.headerTitle.fontWeight ?? '500'),
    // web: topbarIconColor ← modalTopbarIcon (the icon on the top-bar buttons)
    topbarIconColor: content.button.icon.default,
    topbarTextFontSize: px(textVariants.headerTitle.fontSize ?? 13),
    headerIconWrapperSize: px(sizes.buttonXs),
    headerIconSize: px(sizes.iconMd),
    buttonFontWeight: String(textVariants.bodyMedium.fontWeight ?? '500'),
    modalBottomBarButtonHeight: px(sizes.buttonHeight),
    iframeBackground: background.modal,
    // Same fixed paddings web sends (web: modalBaseHorizontalPadding + 6px).
    topbarPaddingX: px(spacing.modalHorizontal + 6),
    topbarPaddingY: '12px',
    boxPaddingY: '16px',
    boxPaddingX: '16px',
    paddingPrimary: '20px',
    paddingSecondary: '12px',
    paddingTertiary: '20px',
  }
}
